import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, MapPin, Star, Clock, Image as ImageIcon } from "lucide-react";
import { groupFacilitiesBySport, gameTypeLabel } from "../lib/facilities";
import MapEmbed, { extractEmbedSrc, searchDirectionsHref } from "../components/MapEmbed";
import MarqueeStrip from "../components/MarqueeStrip";
import ResponsiveImage from "../components/ResponsiveImage";

export default function CentreDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [centre, setCentre] = useState(null);
    const [activeSport, setActiveSport] = useState(null);

    useEffect(() => {
        api.get(`/centres/${id}`).then(({ data }) => {
            setCentre(data);
        });
    }, [id]);

    const grouped = useMemo(() => groupFacilitiesBySport(centre?.facilities || []), [centre]);
    const sportTabs = useMemo(() => Object.keys(grouped), [grouped]);

    useEffect(() => {
        if (!activeSport && sportTabs.length) setActiveSport(sportTabs[0]);
    }, [sportTabs, activeSport]);

    if (!centre) return <div className="p-8 text-center text-slate-400">Loading centre...</div>;

    const facilitiesForSport = grouped[activeSport] || [];
    const galleryUrls = (centre.photos || []).map(p => p?.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`);

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4 text-sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="rounded-lg overflow-hidden border border-slate-800 mb-6">
                <div className="relative h-56 sm:h-72 lg:h-[360px]">
                    <ResponsiveImage
                        src={centre.photo_url}
                        alt={centre.name}
                        eager
                        testid="centre-cover-image"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/30 to-transparent" />
                    {galleryUrls.length > 0 && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-slate-950/80 border border-slate-700 text-slate-200 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">
                            <ImageIcon className="w-3 h-3" /> +{galleryUrls.length} photos
                        </span>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 max-w-[min(900px,95%)]">
                        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white">{centre.name}</h1>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-slate-300">
                            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{centre.address}, {centre.city}</span>
                            <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-[#00FF66] text-[#00FF66]" />{centre.rating}</span>
                            <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4 text-[#00FF66]" />{centre.open_time} – {centre.close_time}</span>
                        </div>
                    </div>
                </div>
            </div>

            {galleryUrls.length > 0 && (
                <div className="mb-6">
                    <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-2">Photos</div>
                    <MarqueeStrip
                        images={centre.photos || []}
                        height={220}
                        gap={12}
                        testid="centre-gallery-marquee"
                    />
                </div>
            )}

            {sportTabs.length > 0 && (
                <div className="mb-6">
                    <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-3">Pick a sport</div>
                    <div className="flex gap-2 flex-wrap">
                        {sportTabs.map((s) => (
                            <button key={s} onClick={() => setActiveSport(s)}
                                data-testid={`sport-tab-${s.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`px-4 h-10 rounded-full text-xs font-bold uppercase tracking-widest border transition-colors ${
                                    activeSport === s ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800 hover:border-[#00FF66]/50"
                                }`}>{s}</button>
                        ))}
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {facilitiesForSport.length === 0 && (
                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 text-center text-slate-500 text-sm">
                        No facilities available for {activeSport} yet.
                    </div>
                )}
                {facilitiesForSport.map((f) => (
                    <div key={f.id} className="flex items-center justify-between bg-slate-900 border border-slate-800 rounded-lg p-4 card-hover" data-testid={`facility-row-${f.id}`}>
                        <div>
                            <div className="font-display font-bold text-lg text-white uppercase">{f.name}</div>
                            <div className="text-xs text-slate-400 mt-0.5">{gameTypeLabel(f.game_type)}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs text-slate-400 uppercase tracking-widest">Per Hour</div>
                            <div className="font-display text-2xl font-bold text-[#00FF66]">₹{f.hourly_price}</div>
                            <button
                                onClick={() => navigate(`/book/centre/${centre.id}/facility/${f.id}`)}
                                data-testid={`book-facility-${f.id}`}
                                className="mt-2 px-4 h-9 bg-[#00FF66] text-black font-bold uppercase text-xs tracking-widest rounded hover:bg-[#00cc52] active:scale-95 transition">
                                Book Now
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Location — moved to the bottom of the page (Iter 26) */}
            <div className="mt-8" data-testid="centre-location-section">
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-2 inline-flex items-center gap-2">
                    <MapPin className="w-3 h-3 text-[#00FF66]" /> Location
                </div>
                <MapEmbed
                    url={extractEmbedSrc(centre.map_embed_url)}
                    height={260}
                    label={centre.name}
                    directionsHref={searchDirectionsHref(`${centre.name}, ${centre.address}, ${centre.city}`)}
                />
            </div>
        </div>
    );
}
